// Fuzzy-Trace Theory (FTT) Processing Mode Classifier
// Implements weighted composite scoring for gist vs verbatim classification

import { MouseTrajectory } from '../types';

export interface FTTClassification {
  mode: 'gist' | 'verbatim' | 'mixed' | 's1_to_s2' | 's2_to_s1';
  confidence: number;
  components: {
    temporal: number;
    trajectory: number;
    contextual: number;
    behavioral: number;
  };
  trajectoryMetrics?: TrajectoryMetrics;
  transitionSignals?: {
    complexityShift: number;
    velocityShift: number;
    hesitation: number;
    earlyRevisits: number;
    convergence: number;
  };
  edgeCaseHandling?: string;
}

export interface TrajectoryMetrics {
  directness: number;
  complexity: number;
  revisits: number;
  microMovements: number;
  pauseRatio: number;
  accelerationChanges: number;
  curvatureEntropy: number;
  velocityConsistency: number;
  decisionPointHesitation: number;
  explorationIndex: number;
  earlyVsLateComplexity: {
    early: number;
    late: number;
  };
}

// ── Tuning constants (CONFIGURABLE — not validated; treat as calibration params) ──
// These are theory-informed defaults, NOT empirically derived. They are collected
// here so they can be recalibrated later in one place (or overridden per task/call
// via classifyProcessingMode's `overrides` arg / the task's fttContext.fttConfig)
// WITHOUT editing the algorithm. Raw inputs (readingTime + every trajectoryMetric)
// are stored per response, so any response can be RE-CLASSIFIED offline once the
// final calibration is chosen. Defaults below reproduce the original behavior.
export interface FTTConfig {
  weights: { temporal: number; trajectory: number; contextual: number; behavioral: number };
  cutoffs: { gist: number; verbatim: number; mixedBand: number };
  // Logistic mapping of response/reading time -> temporal sub-score. `center` is
  // the time (ms) scored as neutral (0.5). 15s suits phishing DECISIONS; news
  // READING is far longer, so info-sharing should set a larger center when known.
  temporal: { center: number; steepness: number };
  transition: { complexityShift: number; hesitationHigh: number; hesitationLow: number; earlyRevisits: number; minConverge: number };
  trajectory: { wDirectness: number; wComplexity: number; wRevisits: number; wPauseRatio: number; wMicroMovements: number; wAccelerationChanges: number };
}
export const DEFAULT_FTT_CONFIG: FTTConfig = {
  weights: { temporal: 0.35, trajectory: 0.30, contextual: 0.20, behavioral: 0.15 },
  cutoffs: { gist: 0.3, verbatim: 0.7, mixedBand: 0.1 },
  temporal: { center: 15000, steepness: 0.0003 },
  transition: { complexityShift: 0.15, hesitationHigh: 0.3, hesitationLow: 0.15, earlyRevisits: 0.1, minConverge: 2 },
  trajectory: { wDirectness: 0.3, wComplexity: 0.25, wRevisits: 0.15, wPauseRatio: 0.15, wMicroMovements: 0.1, wAccelerationChanges: 0.05 },
};

type FTTConfigOverrides = {
  [K in keyof FTTConfig]?: Partial<FTTConfig[K]>;
};

// Weighted composite scoring algorithm with edge case handling
export function classifyProcessingMode(
  responseTime: number,
  trajectory: MouseTrajectory[],
  context: {
    isPhishing: boolean;
    scenarioComplexity: number;
    priorResponses: number;
    cognitiveLoad: number;
  },
  overrides: FTTConfigOverrides = {}
): FTTClassification {
  // Merge overrides over defaults per section (shallow per section is enough).
  const cfg: FTTConfig = {
    weights: { ...DEFAULT_FTT_CONFIG.weights, ...overrides.weights },
    cutoffs: { ...DEFAULT_FTT_CONFIG.cutoffs, ...overrides.cutoffs },
    temporal: { ...DEFAULT_FTT_CONFIG.temporal, ...overrides.temporal },
    transition: { ...DEFAULT_FTT_CONFIG.transition, ...overrides.transition },
    trajectory: { ...DEFAULT_FTT_CONFIG.trajectory, ...overrides.trajectory },
  };
  const weights = cfg.weights;

  // 1. Temporal Component (0-1 scale)
  const temporalScore = calculateTemporalScore(responseTime, cfg.temporal);

  // 2. Trajectory Component
  const trajectoryMetrics = analyzeTrajectory(trajectory);
  const trajectoryScore = calculateTrajectoryScore(trajectoryMetrics, cfg.trajectory);

  // 3. Contextual Component
  const contextualScore = calculateContextualScore(context);

  // 4. Behavioral Component
  const behavioralScore = calculateBehavioralScore(trajectory, responseTime);

  // Weighted composite score
  const compositeScore = 
    (temporalScore * weights.temporal) +
    (trajectoryScore * weights.trajectory) +
    (contextualScore * weights.contextual) +
    (behavioralScore * weights.behavioral);

  // Classification with confidence
  let mode: FTTClassification['mode'];
  let confidence: number;
  let edgeCaseHandling: string | undefined;

  // Edge case handling
  if (Math.abs(compositeScore - 0.5) < cfg.cutoffs.mixedBand) {
    // Mixed processing detected
    mode = 'mixed';
    confidence = 1 - Math.abs(compositeScore - 0.5) / cfg.cutoffs.mixedBand;
    edgeCaseHandling = 'Near boundary - likely switching between modes';
  } else if (compositeScore < cfg.cutoffs.gist) {
    mode = 'gist';
    confidence = 1 - compositeScore;
  } else if (compositeScore > cfg.cutoffs.verbatim) {
    mode = 'verbatim';
    confidence = compositeScore;
  } else {
    // Determine based on dominant component
    const components = { temporalScore, trajectoryScore, contextualScore, behavioralScore };
    const dominantComponent = Object.entries(components)
      .reduce((a, b) => a[1] > b[1] ? a : b)[0];
    
    if (dominantComponent === 'temporalScore' || dominantComponent === 'trajectoryScore') {
      mode = compositeScore < 0.5 ? 'gist' : 'verbatim';
    } else {
      mode = 'mixed';
      edgeCaseHandling = `Dominant component: ${dominantComponent}`;
    }
    confidence = Math.abs(compositeScore - 0.5) * 2;
  }

  // Handle very fast responses with complex trajectories (potential false gist)
  if (responseTime < 1000 && trajectoryMetrics.complexity > 0.7) {
    mode = 'mixed';
    confidence *= 0.7;
    edgeCaseHandling = 'Fast response with complex trajectory - possible practiced behavior';
  }

  // --- Transition Detection (H5) ---
  const complexityShift = trajectoryMetrics.earlyVsLateComplexity.late
                        - trajectoryMetrics.earlyVsLateComplexity.early;
  const earlyVelocity = averageVelocityInRange(trajectory, 0, 0.3);
  const lateVelocity = averageVelocityInRange(trajectory, 0.7, 1.0);
  const velocityShift = earlyVelocity - lateVelocity; // positive = deceleration
  const hesitation = trajectoryMetrics.decisionPointHesitation;
  const earlyRevisits = calculateEarlyRevisits(trajectory);

  // Count converging signals for S1→S2 (gist→verbatim override)
  const s1s2_signals = [
    complexityShift > cfg.transition.complexityShift ? 1 : 0,   // complexity increased
    velocityShift > 0 ? 1 : 0,                                   // decelerated
    hesitation > cfg.transition.hesitationHigh ? 1 : 0           // hesitated at decision point
  ];
  const s2s1_signals = [
    complexityShift < -cfg.transition.complexityShift ? 1 : 0,  // complexity decreased
    velocityShift < 0 ? 1 : 0,                                   // accelerated
    hesitation < cfg.transition.hesitationLow ? 1 : 0            // no hesitation at decision
  ];

  const s1s2_convergence = s1s2_signals.reduce((a, b) => a + b, 0);
  const s2s1_convergence = s2s1_signals.reduce((a, b) => a + b, 0);

  // Require at least `minConverge` of 3 signals to converge
  if (s1s2_convergence >= cfg.transition.minConverge && s2s1_convergence < cfg.transition.minConverge) {
    mode = 's1_to_s2';
    edgeCaseHandling = `S1→S2 override detected (${s1s2_convergence}/3 signals)`;
    confidence = s1s2_convergence / 3;
  } else if (s2s1_convergence >= cfg.transition.minConverge && s1s2_convergence < cfg.transition.minConverge && earlyRevisits > cfg.transition.earlyRevisits) {
    // earlyRevisits check: distinguish genuine S2→S1 collapse from "read then click"
    mode = 's2_to_s1';
    edgeCaseHandling = `S2→S1 collapse detected (${s2s1_convergence}/3 signals)`;
    confidence = s2s1_convergence / 3;
  }

  const transitionSignals = {
    complexityShift,
    velocityShift,
    hesitation,
    earlyRevisits,
    convergence: Math.max(s1s2_convergence, s2s1_convergence)
  };

  return {
    mode,
    confidence,
    components: {
      temporal: temporalScore,
      trajectory: trajectoryScore,
      contextual: contextualScore,
      behavioral: behavioralScore
    },
    trajectoryMetrics,
    transitionSignals,
    edgeCaseHandling
  };
}

function calculateTemporalScore(responseTime: number, temporal: FTTConfig['temporal']): number {
  // Logistic mapping of response/reading time -> temporal sub-score. `center` is the
  // neutral point; below it leans gist, above it leans verbatim. Default 15s suits
  // phishing decisions; configure a larger center for the longer news-reading task.
  return 1 / (1 + Math.exp(-temporal.steepness * (responseTime - temporal.center)));
}

export function analyzeTrajectory(trajectory: MouseTrajectory[]): TrajectoryMetrics {
  if (trajectory.length < 2) {
    return {
      directness: 1,
      complexity: 0,
      revisits: 0,
      microMovements: 0,
      pauseRatio: 0,
      accelerationChanges: 0,
      curvatureEntropy: 0,
      velocityConsistency: 0,
      decisionPointHesitation: 0,
      explorationIndex: 0,
      earlyVsLateComplexity: { early: 0, late: 0 }
    };
  }

  // Calculate total distance and direct distance
  const totalDistance = trajectory.reduce((sum, point, i) => {
    if (i === 0) return 0;
    const prev = trajectory[i - 1];
    return sum + Math.sqrt(Math.pow(point.x - prev.x, 2) + Math.pow(point.y - prev.y, 2));
  }, 0);

  const directDistance = Math.sqrt(
    Math.pow(trajectory[trajectory.length - 1].x - trajectory[0].x, 2) +
    Math.pow(trajectory[trajectory.length - 1].y - trajectory[0].y, 2)
  );

  const directness = directDistance > 0 ? directDistance / totalDistance : 0;

  // Calculate complexity using curvature and direction changes
  let directionChanges = 0;
  let curvatureSum = 0;
  let microMovements = 0;
  let pauses = 0;
  let accelerationChanges = 0;
  let lastAcceleration = 0;

  for (let i = 2; i < trajectory.length; i++) {
    const p1 = trajectory[i - 2];
    const p2 = trajectory[i - 1];
    const p3 = trajectory[i];

    // Direction change
    const angle1 = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    const angle2 = Math.atan2(p3.y - p2.y, p3.x - p2.x);
    const angleDiff = Math.abs(angle2 - angle1);
    if (angleDiff > Math.PI / 4) directionChanges++;

    // Curvature
    const curvature = calculateCurvature(p1, p2, p3);
    curvatureSum += curvature;

    // Micro movements (< 5 pixels)
    const dist = Math.sqrt(Math.pow(p3.x - p2.x, 2) + Math.pow(p3.y - p2.y, 2));
    if (dist < 5 && dist > 0) microMovements++;

    // Pauses (velocity near 0)
    if (p3.velocity < 0.1) pauses++;

    // Acceleration changes
    const acceleration = p3.velocity - p2.velocity;
    if (Math.sign(acceleration) !== Math.sign(lastAcceleration) && lastAcceleration !== 0) {
      accelerationChanges++;
    }
    lastAcceleration = acceleration;
  }

  // Calculate revisits to areas
  const gridSize = 50; // 50x50 pixel grid
  const visitedCells = new Set<string>();
  const cellVisits = new Map<string, number>();
  let revisits = 0;

  trajectory.forEach(point => {
    const cellKey = `${Math.floor(point.x / gridSize)},${Math.floor(point.y / gridSize)}`;
    if (visitedCells.has(cellKey)) {
      revisits++;
    } else {
      visitedCells.add(cellKey);
    }
    cellVisits.set(cellKey, (cellVisits.get(cellKey) || 0) + 1);
  });

  // Calculate exploration index
  const bbox = calculateBoundingBox(trajectory);
  const possibleCells = Math.ceil(bbox.width / gridSize) * Math.ceil(bbox.height / gridSize);
  const explorationIndex = possibleCells > 0 ? visitedCells.size / possibleCells : 0;

  // Calculate curvature entropy
  const curvatures = [];
  for (let i = 2; i < trajectory.length; i++) {
    curvatures.push(calculateCurvature(trajectory[i-2], trajectory[i-1], trajectory[i]));
  }
  const curvatureEntropy = calculateEntropy(curvatures);

  // Calculate velocity consistency
  const velocities = trajectory.map(p => p.velocity);
  const velocityConsistency = calculateStandardDeviation(velocities);

  // Decision point hesitation (last 20% of trajectory)
  const decisionLen = Math.max(Math.floor(trajectory.length * 0.2), 1);
  const decisionSegment = trajectory.slice(-decisionLen);
  const mainSegment = trajectory.slice(0, -decisionLen);
  const decisionVelocity = average(decisionSegment.map(p => p.velocity));
  const mainVelocity = average(mainSegment.map(p => p.velocity));
  const decisionPointHesitation = mainVelocity > 0 ? 1 - (decisionVelocity / mainVelocity) : 0;

  // Early vs late complexity
  const earlySegment = trajectory.slice(0, Math.floor(trajectory.length * 0.3));
  const lateSegment = trajectory.slice(-Math.floor(trajectory.length * 0.3));

  const complexity = (directionChanges + curvatureSum / trajectory.length) / 2;
  const pauseRatio = pauses / trajectory.length;

  return {
    directness,
    complexity: Math.min(complexity, 1),
    revisits: revisits / trajectory.length,
    microMovements: microMovements / trajectory.length,
    pauseRatio,
    accelerationChanges: accelerationChanges / trajectory.length,
    curvatureEntropy,
    velocityConsistency,
    decisionPointHesitation: Math.max(0, decisionPointHesitation),
    explorationIndex,
    earlyVsLateComplexity: {
      early: calculateComplexity(earlySegment),
      late: calculateComplexity(lateSegment)
    }
  };
}

function calculateCurvature(p1: MouseTrajectory, p2: MouseTrajectory, p3: MouseTrajectory): number {
  // Menger curvature formula
  const area = Math.abs(
    (p2.x - p1.x) * (p3.y - p1.y) - (p3.x - p1.x) * (p2.y - p1.y)
  ) / 2;
  
  const a = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  const b = Math.sqrt(Math.pow(p3.x - p2.x, 2) + Math.pow(p3.y - p2.y, 2));
  const c = Math.sqrt(Math.pow(p3.x - p1.x, 2) + Math.pow(p3.y - p1.y, 2));
  
  if (a * b * c === 0) return 0;
  return 4 * area / (a * b * c);
}

function calculateTrajectoryScore(metrics: TrajectoryMetrics, w: FTTConfig['trajectory']): number {
  // Higher directness = more gist-like (lower score)
  // Higher complexity = more verbatim-like (higher score)
  const score =
    (1 - metrics.directness) * w.wDirectness +
    metrics.complexity * w.wComplexity +
    metrics.revisits * w.wRevisits +
    metrics.pauseRatio * w.wPauseRatio +
    metrics.microMovements * w.wMicroMovements +
    metrics.accelerationChanges * w.wAccelerationChanges;

  return Math.min(Math.max(score, 0), 1);
}

function calculateContextualScore(context: {
  isPhishing: boolean;
  scenarioComplexity: number;
  priorResponses: number;
  cognitiveLoad: number;
}): number {
  let score = 0;

  // NOTE: a ground-truth veracity term (`if (context.isPhishing) score += 0.2`) was
  // removed here — feeding an independent variable into the processing-mode score makes
  // the mode DV circular. All callers now pass isPhishing:false; the field is retained
  // on the context type only for backward compatibility and is intentionally unused.

  // Higher complexity encourages verbatim. Callers pass a uniform constant (0.5), so this
  // contributes a constant offset and does not leak design-confounded complexity into the DV.
  score += context.scenarioComplexity * 0.3;

  // Fatigue effect - more prior responses = more gist
  score -= Math.min(context.priorResponses * 0.02, 0.3);

  // High cognitive load pushes toward gist
  score -= context.cognitiveLoad * 0.2;

  return Math.min(Math.max(score, 0), 1);
}

function calculateBehavioralScore(
  trajectory: MouseTrajectory[],
  responseTime: number
): number {
  let score = 0;

  // Check for reading pattern (systematic scanning)
  const verticalMovements = trajectory.filter((p, i) => 
    i > 0 && Math.abs(p.y - trajectory[i-1].y) > Math.abs(p.x - trajectory[i-1].x)
  ).length;
  
  if (verticalMovements / trajectory.length > 0.3) {
    score += 0.3; // Systematic reading suggests verbatim
  }

  // Check for hesitation near decision point
  const lastQuarter = trajectory.slice(-Math.floor(trajectory.length / 4));
  const avgVelocityLastQuarter = average(lastQuarter.map(p => p.velocity));
  const avgVelocityOverall = average(trajectory.map(p => p.velocity));
  
  if (avgVelocityLastQuarter < avgVelocityOverall * 0.5) {
    score += 0.3; // Slowing down suggests deliberation
  }

  // Very consistent velocity suggests automatic processing
  const velocityVariance = calculateVariance(trajectory.map(p => p.velocity));
  if (velocityVariance < 0.1) {
    score -= 0.2;
  }

  return Math.min(Math.max(score, 0), 1);
}

function calculateVariance(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  return values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
}

function calculateBoundingBox(trajectory: MouseTrajectory[]): { width: number; height: number } {
  if (trajectory.length === 0) return { width: 0, height: 0 };
  
  const xs = trajectory.map(p => p.x);
  const ys = trajectory.map(p => p.y);
  
  return {
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys)
  };
}

function calculateEntropy(values: number[]): number {
  if (values.length === 0) return 0;
  
  const bins = 10;
  const min = Math.min(...values);
  const max = Math.max(...values);
  
  if (max === min) return 0;
  
  const binSize = (max - min) / bins;
  const histogram = new Array(bins).fill(0);
  
  values.forEach(v => {
    const binIndex = Math.min(Math.floor((v - min) / binSize), bins - 1);
    histogram[binIndex]++;
  });
  
  const probabilities = histogram.map(count => count / values.length);
  return -probabilities.reduce((sum, p) => sum + (p > 0 ? p * Math.log2(p) : 0), 0);
}

function calculateStandardDeviation(values: number[]): number {
  const variance = calculateVariance(values);
  return Math.sqrt(variance);
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

function calculateComplexity(segment: MouseTrajectory[]): number {
  if (segment.length < 3) return 0;
  
  let directionChanges = 0;
  for (let i = 2; i < segment.length; i++) {
    const angle1 = Math.atan2(segment[i-1].y - segment[i-2].y, segment[i-1].x - segment[i-2].x);
    const angle2 = Math.atan2(segment[i].y - segment[i-1].y, segment[i].x - segment[i-1].x);
    if (Math.abs(angle2 - angle1) > Math.PI / 4) directionChanges++;
  }
  
  return directionChanges / (segment.length - 2);
}

function averageVelocityInRange(trajectory: MouseTrajectory[], startFrac: number, endFrac: number): number {
  if (trajectory.length < 2) return 0;
  const startIdx = Math.floor(trajectory.length * startFrac);
  const endIdx = Math.floor(trajectory.length * endFrac);
  const segment = trajectory.slice(startIdx, endIdx);
  if (segment.length === 0) return 0;
  return segment.reduce((sum, p) => sum + (p.velocity || 0), 0) / segment.length;
}

function calculateEarlyRevisits(trajectory: MouseTrajectory[]): number {
  if (trajectory.length < 2) return 0;
  const earlyEnd = Math.floor(trajectory.length * 0.3);
  const earlySegment = trajectory.slice(0, earlyEnd);
  if (earlySegment.length === 0) return 0;

  const gridSize = 50;
  const visitedCells = new Set<string>();
  let revisits = 0;

  earlySegment.forEach(point => {
    const cellKey = `${Math.floor(point.x / gridSize)},${Math.floor(point.y / gridSize)}`;
    if (visitedCells.has(cellKey)) {
      revisits++;
    } else {
      visitedCells.add(cellKey);
    }
  });

  return earlySegment.length > 0 ? revisits / earlySegment.length : 0;
}

// Export for testing
export const testHelpers = {
  calculateTemporalScore,
  calculateTrajectoryScore,
  calculateContextualScore,
  calculateBehavioralScore
};